import { createCheckoutContext, sameCheckoutContext } from './context.js'
import { normalizePickupPoint, withPickupPointCountry } from './pickupPoint.js'

export const CHECKOUT_SESSION_VERSION = 1

function storageKey(shopId) {
  return `octolize-dhl:checkout:v${CHECKOUT_SESSION_VERSION}:${encodeURIComponent(String(shopId ?? '').trim())}`
}

function cartMatches(storedContext, currentContext) {
  return !storedContext.cartKey || !currentContext.cartKey || storedContext.cartKey === currentContext.cartKey
}

function contextMatches(storedContext, currentContext) {
  const withoutCart = (context) => ({ ...context, cartKey: null, stage: 'other' })
  return cartMatches(storedContext, currentContext)
    && sameCheckoutContext(withoutCart(storedContext), withoutCart(currentContext))
}

export function createCheckoutSessionStore({ storage = globalThis.sessionStorage, shopId, logger = console } = {}) {
  const key = storageKey(shopId)

  function read() {
    try {
      const record = JSON.parse(storage?.getItem(key) ?? 'null')
      return record?.version === CHECKOUT_SESSION_VERSION && record.context ? record : null
    } catch (error) {
      logger?.warn?.('Cannot read DHL checkout session', error)
      return null
    }
  }

  function write(record) {
    try {
      storage?.setItem(key, JSON.stringify(record))
      return true
    } catch (error) {
      logger?.warn?.('Cannot write DHL checkout session', error)
      return false
    }
  }

  return {
    key,
    read,
    loadForContext(context) {
      const record = read()
      const normalizedContext = createCheckoutContext(context)
      if (!record) {
        return { status: 'empty', snapshot: null, verifiedCart: false }
      }
      const storedContext = createCheckoutContext(record.context)
      if (!contextMatches(storedContext, normalizedContext)) {
        return { status: 'stale', snapshot: null, verifiedCart: false }
      }

      return {
        status: record.tombstone ? 'cleared' : 'selection',
        snapshot: record.tombstone
          ? null
          : withPickupPointCountry(normalizePickupPoint(record.snapshot), storedContext.countryIso),
        verifiedCart: Boolean(storedContext.cartKey && normalizedContext.cartKey),
        pendingPersistence: Boolean(record.pendingPersistence),
      }
    },
    saveSelection(context, snapshot, { pendingPersistence = false } = {}) {
      const normalizedContext = createCheckoutContext(context)
      const normalizedSnapshot = normalizePickupPoint(snapshot)
      if (!normalizedSnapshot) {
        return false
      }
      return write({
        version: CHECKOUT_SESSION_VERSION,
        context: normalizedContext,
        snapshot: withPickupPointCountry(normalizedSnapshot, normalizedContext.countryIso),
        tombstone: false,
        pendingPersistence,
      })
    },
    markCleared(context) {
      return write({
        version: CHECKOUT_SESSION_VERSION,
        context: createCheckoutContext(context),
        snapshot: null,
        tombstone: true,
      })
    },
    clear() {
      try {
        storage?.removeItem(key)
        return true
      } catch (error) {
        logger?.warn?.('Cannot clear DHL checkout session', error)
        return false
      }
    },
  }
}
